import firebase from 'firebase/app';
import _ from 'lodash';
import { getUserAll, getTeamByCompany, invalidateMemberAndTeamCaches } from './cacheService';
import { resolveTeamHeightFromTeams } from '@/utils/teamHeight';
import format from 'date-fns/format';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

export const API_NODE_BASE_URL = process.env.VUE_APP_API_URL || 'https://api-node.happily.ai'

type ApiNodeRequestOptions = {
  auth?: boolean;
}

const hasHeader = (headers: any, headerName: string): boolean => {
  if (!headers) return false
  const target = headerName.toLowerCase()
  return Object.keys(headers).some((key) => key.toLowerCase() === target)
}

const isFormDataPayload = (payload: any): boolean => {
  return typeof FormData !== 'undefined' && payload instanceof FormData
}

const buildApiNodeUrl = (url?: string): string => {
  if (!url) return API_NODE_BASE_URL
  if (/^https?:\/\//i.test(url)) return url
  return `${API_NODE_BASE_URL}${url.charAt(0) === '/' ? '' : '/'}${url}`
}

export const getApiNodeAuthHeaders = async (): Promise<{ Authorization: string }> => {
  const currentUser = firebase.auth().currentUser
  if (!currentUser) {
    throw new Error('Please sign in again to continue.')
  }

  const token = await currentUser.getIdToken()
  return {
    Authorization: `Bearer ${token}`,
  }
}

export const apiNodeRequest = async <T = any>(
  config: AxiosRequestConfig,
  options: ApiNodeRequestOptions = {}
): Promise<AxiosResponse<T>> => {
  const headers = Object.assign({}, config.headers || {})

  if (!hasHeader(headers, 'Content-Type') && !isFormDataPayload(config.data)) {
    headers['Content-Type'] = 'application/json'
  }

  if (options.auth !== false) {
    Object.assign(headers, await getApiNodeAuthHeaders())
  }

  return axios(Object.assign({}, config, {
    url: buildApiNodeUrl(config.url),
    headers,
  }))
}

export default {
  namespaced: true,
  state: {
    allUser:{},
    status:false,
    allTeam:{},
    teamHeight:'',
    organization:{},
    language:{}
  },
  mutations: {
    setAllUser(state:any, result:any) {
        state.allUser = result
      },
    setAllTeam(state:any, result:any) {
        state.allTeam = result
      },
      setTeamHeight(state:any, result:any) {
        state.teamHeight = result
      },
      setOrganization(state:any, result:any) {
        state.organization = result
      },
      setLanguage(state:any, result:any) {
        state.language = result
      }
  },
  actions: {
    async getuser({ commit, state, rootState }: any, force: boolean = false) {
      // If data already exists and force is false, resolve immediately
      if (!force && state.allUser && Object.keys(state.allUser).length > 0) {
        return Promise.resolve(state.allUser);
      }
       
      try {
        if (force) {
          invalidateMemberAndTeamCaches(rootState.authen.user.company)
        }

        const [userData, teamData] = await Promise.all([
          getUserAll(rootState.authen.user.company),
          getTeamByCompany(rootState.authen.user.company)
        ])
        
        
       
          commit('setAllUser', userData);
          commit('setAllTeam', teamData);
          const teamHeight = resolveTeamHeightFromTeams(teamData)
          if (teamHeight) {
            commit('setTeamHeight', teamHeight)
          }
        return userData
        
        //return Promise.resolve(userData || {});
      } catch (error) {
        console.error('Error loading user data:', error);
        return Promise.reject(error);
      }
    },
    async removeUser({commit,state,rootState,dispatch}:any,update:any){
        return new Promise((resolve, reject) => {
         
            var data = {
              "c": rootState.authen.user.company,
              "t": "users"
            };
            const config: AxiosRequestConfig = {
              method: 'post',
              url: '/cache/removeTable',
              data : data
            };
           apiNodeRequest(config)
            .then(response => {
              //console.log("data")
                const data = response.data
                resolve(data);
                dispatch("getuser",true)
            })
            .catch(err => {
              console.log("err",err)
                reject({ errorMessage: err.message });
            })
          
   
      });
  
  
  
  
    },
    async getteam({commit,state,rootState,dispatch}:any, force: boolean = false){
        if (!force && state.allTeam && Object.keys(state.allTeam).length > 0) {
          return state.allTeam
        }

        const teamData = await getTeamByCompany(rootState.authen.user.company)
        commit('setAllTeam', teamData)
        const teamHeight = resolveTeamHeightFromTeams(teamData)
        if (teamHeight) {
          commit('setTeamHeight', teamHeight)
        }
        return teamData
    },
    /**
     * Canonical teamHeight loader.
     * - Recomputes from provided `teams` when present (keeps value fresh after org edits).
     * - Otherwise reuses main.teamHeight / main.allTeam unless `force`.
     */
    async ensureTeamHeight({commit, state, rootState, dispatch}: any, payload: any = {}) {
        const force = !!(payload && payload.force)
        const teamsFromPayload = payload && payload.teams

        if (teamsFromPayload && (Array.isArray(teamsFromPayload) ? teamsFromPayload.length > 0 : Object.keys(teamsFromPayload).length > 0)) {
          if (!Array.isArray(teamsFromPayload) && (!state.allTeam || Object.keys(state.allTeam).length === 0 || force)) {
            commit('setAllTeam', teamsFromPayload)
          }
          const teamHeight = resolveTeamHeightFromTeams(teamsFromPayload)
          if (teamHeight) {
            commit('setTeamHeight', teamHeight)
          }
          return teamHeight || ''
        }

        if (!force && state.teamHeight) {
          return state.teamHeight
        }

        const teamData = await dispatch('getteam', force)
        const teamHeight = resolveTeamHeightFromTeams(teamData)
        if (teamHeight) {
          commit('setTeamHeight', teamHeight)
        }
        return teamHeight || ''
    },
    async getteamHeight({dispatch}: any, payload: any = {}) {
        return dispatch('ensureTeamHeight', payload)
    },
    /** Force-reload teams + recompute root after create/update/delete team. */
    async refreshTeamsAndHeight({commit, dispatch, rootState}: any) {
        commit('setTeamHeight', '')
        commit('setAllTeam', {})
        // Clear module-local caches so next page load recomputes from fresh teams
        const clearLocal = [
          'dashboard/setTeamHeight',
          'report/setTeamHeight',
          'teams/setTeamHeight',
          'teams/setTeamHeightModal',
          'question/setTeamHeight',
          'people/setTeamHeight',
          'managers/setTeamHeight',
          'insightsManagers/setTeamHeight',
          'insightsCohorts/setTeamHeight',
          'monthlyStrength/setTeamHeight',
        ]
        clearLocal.forEach((type) => {
          try { commit(type, '', { root: true }) } catch (e) { /* module may be unregistered */ }
        })
        try { commit('powerSkill/setteamHeight', '', { root: true }) } catch (e) { /* optional */ }
        try { commit('skillOverview/setteamHeight', '', { root: true }) } catch (e) { /* optional */ }

        if (rootState.authen && rootState.authen.user && rootState.authen.user.company) {
          invalidateMemberAndTeamCaches(rootState.authen.user.company)
        }
        return dispatch('ensureTeamHeight', { force: true })
    },
    async getOrganization({commit,state,rootState}:any){
      const result={}
      // console.log('getOrganization')
      await firebase.firestore().collection('organization1level').where("company", "==", rootState.authen.user.company).get()
      .then((querySnapshot)=> {
              querySnapshot.forEach((doc)=> {
                const team_id=doc.data().team_id
                if(!result[team_id]){
                  result[team_id]=doc.data().parent
                }
              });
              //console.log('result',result)
              commit('setOrganization', result)  
      }); return result
    },
    async getLanguage({commit,state,rootState}:any){
      const LANG_FALLBACK:any = {
        'th': {name: 'Thai', code: 'th'},
        'ms': {name: 'Bahasa Malaysia', code: 'ms'},
        'zh-HK': {name: 'Chinese (Traditional, Hong Kong)', code: 'zh-HK'},
        'pt-br': {name: 'Brazilian Portuguese', code: 'pt-br'},
        'my': {name: 'Burmese', code: 'my'}
      }
      const materlanguage=(await firebase.database().ref(`/language`).once('value')).val()
            let userLanguage=await getUserAll(rootState.authen.user.company)//(await firebase.database().ref('users').orderByChild("company").equalTo(rootState.authen.user.company).once('value')).val()
            userLanguage=_.groupBy(userLanguage,'language')
            const languages:Array<any>=[]
            const lang_code={}
            for(const i in userLanguage){
                if(i === 'undefined' || !i) continue
                if(materlanguage[i]){
                    languages.push({name:materlanguage[i].name,code:materlanguage[i].code})
                    lang_code[materlanguage[i].name]=materlanguage[i].code
                } else if(LANG_FALLBACK[i]){
                    languages.push({name:LANG_FALLBACK[i].name,code:LANG_FALLBACK[i].code})
                    lang_code[LANG_FALLBACK[i].name]=LANG_FALLBACK[i].code
                }
            }
            commit('setLanguage',{languages,lang_code})
    },
    async addAndUpdateGoalTeam({commit,state,rootState}:any,payload: {teamId: string, goals: string}){
      const {teamId, goals} = payload;
      let newId: string = '';
      if(goals && teamId){
        newId = firebase.firestore().collection('goals_team').doc().id;
        await firebase.firestore().collection('goals_team').doc(newId).set({
          team_id:teamId,
          company:rootState.authen.user.company,
          user_id:rootState.authen.user.email,
          createdate:format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          goals:goals
        });
        
        try {
          await apiNodeRequest({
            method: 'post',
            url: '/openai/get_goals_team',
            data: {
            company_id:rootState.authen.user.company,
            team_id:teamId,
            goals:goals,
            goal_team_id:newId
            }
          });
          console.log('addAndUpdateGoalTeam success');
        } catch (error) {
          console.log('addAndUpdateGoalTeam error', error);
          throw error; // ส่ง error ต่อเพื่อให้ caller รู้
        }
      }
    }
  
  },
  getters: {
    allUser : (state:any) => {
      return state.allUser
    },
    allTeam : (state:any) => {
      return state.allTeam
    },
    teamHeight : (state:any) => {
      return state.teamHeight
    },
    organization : (state:any) => {
      return state.organization
    },
    language : (state:any) => {
      return state.language
    }
  }
}
